<script setup lang="ts">
import type { BlockFaqsFaq } from '#shared/types/schema';
import Text from '~/components/base/Text.vue';
import Tagline from '~/components/base/Tagline.vue';
import Headline from '~/components/base/Headline.vue';

interface BlockFaqsProps {
	data: {
		id?: string | number;
		headline?: string | null;
		block_faqs?: BlockFaqsFaq[] | (string | number)[];
	};
	background?: 'light' | 'dark' | null;
}

const props = defineProps<BlockFaqsProps>();
const { setAttr } = useVisualEditing();

// Stato per gestire quali accordion sono aperti
const openAccordions = ref<Set<number>>(new Set());

// Funzione per toggle dell'accordion
const toggleAccordion = (faqId: number) => {
	if (openAccordions.value.has(faqId)) {
		openAccordions.value.delete(faqId);
	} else {
		openAccordions.value.add(faqId);
	}
};

// Filtra e ordina le FAQ valide
const validFaqs = computed(() => {
	if (!props.data.block_faqs) return [];
	
	return props.data.block_faqs
		.filter((faq): faq is BlockFaqsFaq => {
			return typeof faq === 'object' && faq !== null && 'id' in faq;
		})
		.sort((a, b) => {
			const sortA = a.sort ?? 0;
			const sortB = b.sort ?? 0;
			return sortA - sortB;
		});
});

const textColor = computed(() => {
	if (props.background === 'dark') {
		return 'text-white';
	}
	return 'text-black';
});
</script>

<template>
	<section class="py-12 md:py-16 lg:py-20">
		<div class="max-w-7xl mx-auto">
			<div class="text-left mb-12">
				<Headline
					v-if="data.headline"
					:headline="data.headline"
					:text-color="textColor"
					:data-directus="
						data.id
							? setAttr({
									collection: 'block_faqs',
									item: String(data.id),
									fields: 'headline',
									mode: 'popover',
								})
							: undefined
					"
				/>
			</div>

			<div
				v-if="validFaqs.length > 0"
				class="space-y-4"
				:data-directus="
					data.id
						? setAttr({
								collection: 'block_faqs',
								item: String(data.id),
								fields: ['block_faqs'],
								mode: 'modal',
							})
						: undefined
				"
			>
				<div
					v-for="faq in validFaqs"
					:key="faq.id"
					class="border-b border-gray-200 dark:border-gray-700"
					:class="textColor === 'text-white' ? 'border-gray-600' : 'border-gray-200'"
				>
					<button
						@click="toggleAccordion(faq.id)"
						class="w-full flex items-center justify-between py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 transition-colors"
						:aria-expanded="openAccordions.has(faq.id)"
						:aria-controls="`faq-answer-${faq.id}`"
					>
						<span
							v-if="faq.question"
							class="font-heading font-normal uppercase text-base md:text-lg text-accent pr-4"
							:data-directus="
								data.id && faq.id
									? setAttr({
											collection: 'block_faqs_faqs',
											item: faq.id,
											fields: 'question',
											mode: 'popover',
										})
									: undefined
							"
						>
							{{ faq.question }}
						</span>
						<svg
							class="w-5 h-5 transition-transform duration-200 flex-shrink-0 text-accent"
							:class="[
								openAccordions.has(faq.id) ? 'rotate-180' : ''
							]"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M19 9l-7 7-7-7"
							/>
						</svg>
					</button>
					<div
						:id="`faq-answer-${faq.id}`"
						class="overflow-hidden transition-all duration-300 ease-in-out"
						:class="[
							openAccordions.has(faq.id) ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
						]"
					>
						<div
							class="pb-4 w-full"
							:class="textColor"
						>
							<Text
								v-if="faq.answer"
								:content="faq.answer"
								:text-color="textColor"
								class="w-full max-w-none text-base prose-sm md:max-w-none lg:max-w-none [&_p]:!mb-0 [&_p+_p]:!mt-2 [&_p]:max-w-none [&_p]:w-full [&_ul]:max-w-none [&_ul]:w-full [&_ol]:max-w-none [&_ol]:w-full [&_li]:max-w-none [&_li]:w-full prose-p:text-base prose-ul:text-base prose-li:text-base"
								:class="[
									textColor === 'text-white' 
										? 'prose-p:text-white prose-headings:text-white prose-ul:text-white prose-ol:text-white prose-li:text-white prose-blockquote:text-white' 
										: 'prose-p:text-black prose-headings:text-black prose-ul:text-black prose-ol:text-black prose-li:text-black prose-blockquote:text-black'
								]"
								:data-directus="
									data.id && faq.id
										? setAttr({
												collection: 'block_faqs_faqs',
												item: faq.id,
												fields: 'answer',
												mode: 'drawer',
											})
										: undefined
								"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

